import { useEffect, useState, useMemo, useRef } from 'react';
import { CarouselIndicators } from 'reactstrap';
import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

import './events.css';

// ======== IMPORTANTE ========
// PARA TESTE LOCAL SIMULANDO EVENTOS: ver tutorial no chamado TUNE-11929.
// ===============================================

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie =
    name +
    '=' +
    encodeURIComponent(value) +
    '; expires=' +
    expires +
    '; path=/';
}

function getCookie(name) {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
}

const isDastEmpty = (dastValue) => {
  if (!dastValue?.document) return true;
  const extractText = (node) => {
    if (node.type === 'span') return node.value || '';
    if (node.children) return node.children.map(extractText).join('');
    return '';
  };
  return extractText(dastValue.document).trim() === '';
};

const renderDastNode = (node, index) => {
  if (!node) return null;

  switch (node.type) {
    case 'root':
      return (
        <div key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </div>
      );

    case 'paragraph':
      return (
        <p key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </p>
      );

    case 'heading': {
      const Tag = `h${node.level}`;
      return (
        <Tag key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </Tag>
      );
    }

    case 'list':
      if (node.style === 'bulleted') {
        return (
          <ul key={index}>
            {node.children.map((child, i) => renderDastNode(child, i))}
          </ul>
        );
      }
      return (
        <ol key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </ol>
      );

    case 'listItem':
      return (
        <li key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </li>
      );

    case 'blockquote':
      return (
        <blockquote key={index}>
          {node.children.map((child, i) => renderDastNode(child, i))}
        </blockquote>
      );

    case 'code':
      return (
        <pre key={index}>
          <code>{node.code}</code>
        </pre>
      );

    case 'thematicBreak':
      return <hr key={index} />;

    case 'link':
      return (
        <a
          key={index}
          href={node.url}
          target={node.meta?.find((m) => m.id === 'target')?.value || '_self'}
          rel="noreferrer"
        >
          {node.children.map((child, i) => renderDastNode(child, i))}
        </a>
      );

    case 'span': {
      let content = <>{node.value}</>;
      if (node.marks?.includes('strong')) content = <strong>{content}</strong>;
      if (node.marks?.includes('emphasis')) content = <em>{content}</em>;
      if (node.marks?.includes('underline')) content = <u>{content}</u>;
      if (node.marks?.includes('strikethrough')) content = <s>{content}</s>;
      if (node.marks?.includes('code')) content = <code>{content}</code>;
      if (node.marks?.includes('highlight')) content = <mark>{content}</mark>;
      return <span key={index}>{content}</span>;
    }

    default:
      return null;
  }
};

export default function Eventos() {
  const { user, modules } = useAuth();
  const [events, setEvents] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [bannerTimeOut, setBannerTimeOut] = useState(null);
  const slideRef = useRef(null);

  const transicaoAutomatica = true;
  const tempoTransicaoAutomatica = 20;

  const intl = useIntl();

  const visibleEvents = useMemo(() => {
    if (!user || !modules || modules.length === 0) return [];

    const now = new Date();
    const userModuleCodes = modules.map((m) => m.id);

    return events
      .map((event) => {
        const cookieBaseName = `evento_${event.id}_${user.id}`;

        //Valida período
        const start = event.startDate ? new Date(event.startDate) : null;
        const end = event.endDate ? new Date(event.endDate) : null;
        if ((start && start > now) || (end && end < now)) return false;

        //Valida usuário admin
        if (event.onlyAdminUser && !user.usuarioPerfilAdmin) return false;

        //Valida módulos
        if (event.enabledModules && event.enabledModules.length > 0) {
          const required = event.enabledModules.map((m) => m.codigo);
          const hasAccess = required.some((code) =>
            userModuleCodes.includes(code)
          );
          if (!hasAccess) return false;
        }

        // Valida cookie
        if (getCookie(`${cookieBaseName}_canceled`)) return false;
        else {
          const postponed = getCookie(`${cookieBaseName}_postponed`);
          if (postponed) {
            const postponedTime = parseInt(postponed, 10);
            const sixteenHoursAgo = Date.now() - 16 * 60 * 60 * 1000; //16 horas de validade
            if (postponedTime > sixteenHoursAgo) return false;
          }
        }

        return { ...event, cookieBaseName };
      })
      .filter(Boolean);
  }, [events, user, modules]);

  const getFieldValue = (event, field) => {
    let suffix = '';
    if (user.idioma === 'pt-br') suffix = 'PtBr';
    else if (user.idioma === 'es') suffix = 'Es';
    else suffix = 'En';

    return event[`${field}${suffix}`] || null;
  };

  const getContentValue = (event) => {
    const content = getFieldValue(event, 'content');
    return content?.value ?? null;
  };

  const getImageUrl = (event) => {
    const image = getFieldValue(event, 'image');
    return image?.url ?? null;
  };

  const handleNext = () => {
    setActiveIndex((current) =>
      current === visibleEvents.length - 1 ? 0 : current + 1
    );
  };

  const getAllActioActiveEvents = async () => {
    let eventos = [];

    try {
      const response = await http.get(`/EventosActio/ObterEventosAtivos`);
      const jsonString = response?.data || '[]';
      eventos = JSON.parse(jsonString);

      //filtra os eventos pelo idioma do usuario
      if (user.idioma === 'pt-br') {
        eventos = eventos.filter((e) => !isDastEmpty(e.contentPtBr?.value));
      } else if (user.idioma === 'es') {
        eventos = eventos.filter((e) => !isDastEmpty(e.contentEs?.value));
      } else {
        eventos = eventos.filter((e) => !isDastEmpty(e.contentEn?.value));
      }
    } catch (err) {
      errorHandler(err);
      eventos = [];
    }
    setEvents(eventos);
  };

  const updateAutomaticCarousel = () => {
    clearTimeout(bannerTimeOut);

    setBannerTimeOut(
      setTimeout(() => {
        handleNext();
      }, tempoTransicaoAutomatica * 1000)
    );
  };

  useEffect(() => {
    if (!user) return;

    getAllActioActiveEvents();
  }, [user]);

  useEffect(() => {
    const hasEvents = visibleEvents?.length > 0;
    if (hasEvents && transicaoAutomatica) updateAutomaticCarousel();
  }, [visibleEvents, activeIndex]);

  useEffect(() => {
    if (activeIndex >= visibleEvents.length && visibleEvents.length > 0) {
      setActiveIndex(0);
    }
  }, [visibleEvents.length, activeIndex]);

  useEffect(() => {
    if (slideRef.current) slideRef.current.scrollTop = 0;
  }, [activeIndex]);

  const currentEvent = visibleEvents[activeIndex];

  const handleCallToAction = () => {
    window.open(getFieldValue(currentEvent, 'callToActionLink'), '_blank');
  };

  const handlePostpone = () => {
    const now = Date.now();
    setCookie(`${currentEvent.cookieBaseName}_postponed`, now.toString(), 365);
    setEvents((prev) => prev.filter((e) => e.id !== currentEvent.id));
  };

  const handleCancel = () => {
    setCookie(`${currentEvent.cookieBaseName}_canceled`, '1', 365);
    setEvents((prev) => prev.filter((e) => e.id !== currentEvent.id));
  };

  if (visibleEvents.length === 0 || !currentEvent) return null;

  const imageUrl = getImageUrl(currentEvent);

  return (
    <Dialog onClose={handleCancel} width="570px">
      <div
        className="p-3 eventos-modal"
        style={{
          width: 550,
          maxHeight: 650,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          ref={slideRef}
          key={currentEvent.id}
          className="ml-3 pr-2 eventos-slide"
          style={{ width: 520, maxHeight: 550, overflowY: 'auto' }}
        >
          {imageUrl && (
            <div className="mb-3">
              <img className="w-100" src={imageUrl} alt="" loading="lazy" />
            </div>
          )}

          {renderDastNode(getContentValue(currentEvent)?.document)}
        </div>

        <div
          className="d-flex justify-content-end gap-2 mt-2 mr-6"
          style={{ width: '100%' }}
        >
          <Button
            color="secondary"
            className="rounded-lg"
            onClick={handlePostpone}
            style={{ marginRight: '8px' }}
          >
            {intl.formatMessage({ id: 'verMaisTarde' })}
          </Button>
          <Button
            color="secondary"
            className="rounded-lg"
            onClick={handleCancel}
            style={{ marginRight: '8px' }}
          >
            {intl.formatMessage({ id: 'label.cancelar' })}
          </Button>
          <Button
            color="primary"
            className="rounded-lg font-bold"
            onClick={handleCallToAction}
          >
            {getFieldValue(currentEvent, 'callToActionText') ||
              intl.formatMessage({ id: 'verMais' })}
          </Button>
        </div>

        {visibleEvents.length > 1 && (
          <div className="carousel-wrapper">
            <CarouselIndicators
              items={visibleEvents}
              activeIndex={activeIndex}
              onClickHandler={setActiveIndex}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
